import React from 'react';
import { LiveWorldMap } from './LiveWorldMap';
import { EventTicker } from './EventTicker';
import { RealtimeCharts } from './RealtimeCharts';
import { Users, UserPlus, Zap, Globe, Clock, MapPinOff } from 'lucide-react';
import { ActiveVisitor, TodayStats } from '../hooks/useAnalyticsWebSocket';

interface RealtimeDashboardProps {
    connected: boolean;
    recentEvents: any[];
    activeVisitors: ActiveVisitor[];
    todayStats: TodayStats;
}

export const RealtimeDashboard: React.FC<RealtimeDashboardProps> = ({
    connected,
    recentEvents,
    activeVisitors,
    todayStats,
}) => {
    return (
        <div className="flex flex-col gap-6 animate-in fade-in duration-700">
            {/* Real-time Header Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white border border-neutral-100 rounded-2xl p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users size={40} className="text-blue-600" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Active Now</span>
                    </div>
                    <div className="text-4xl font-bold text-neutral-900 leading-none">{activeVisitors.length}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3">
                        <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <span className="text-[10px] font-black text-amber-700/80 uppercase tracking-tighter">
                                {activeVisitors.filter(v => (!v.userType || v.userType === 'anonymous')).length} Guests
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span className="text-[10px] font-black text-emerald-700/80 uppercase tracking-tighter">
                                {activeVisitors.filter(v => v.userType === 'new_signup').length} New
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            <span className="text-[10px] font-black text-blue-700/80 uppercase tracking-tighter">
                                {activeVisitors.filter(v => v.userType === 'existing_user').length} Returning
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-neutral-100 rounded-2xl p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <UserPlus size={40} className="text-emerald-600" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">New Signups</span>
                    </div>
                    <div className="text-4xl font-bold text-neutral-900 leading-none">{todayStats.signups}</div>
                    <div className="text-[12px] font-medium text-neutral-500 mt-2">New users today</div>
                </div>

                <div className="bg-white border border-neutral-100 rounded-2xl p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Zap size={40} className="text-amber-600" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Live Traffic</span>
                    </div>
                    <div className="text-4xl font-bold text-neutral-900 leading-none">{todayStats.totalEvents}</div>
                    <div className="text-[12px] font-medium text-neutral-500 mt-2">Total events tracked</div>
                </div>

                <div className="bg-white border border-neutral-100 rounded-2xl p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all text-neutral-900">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <MapPinOff size={40} className="text-neutral-600" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-neutral-400 shadow-[0_0_8px_rgba(163,163,163,0.5)]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Hidden Locations</span>
                    </div>
                    <div className="text-4xl font-bold leading-none">
                        {activeVisitors.filter(v => !v.location?.located).length}
                    </div>
                    <div className="text-[12px] font-medium text-neutral-500 mt-2">Unable to geolocate</div>
                </div>

                <div className="bg-white border border-neutral-100 rounded-2xl p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Globe size={40} className={connected ? 'text-blue-600' : 'text-neutral-400'} />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className={`w-2 h-2 rounded-full ${connected ? 'bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.5)]' : 'bg-neutral-300'}`} />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Connection Status</span>
                    </div>
                    <div className={`text-2xl font-black uppercase tracking-tight ${connected ? 'text-blue-600' : 'text-neutral-400'}`}>
                        {connected ? 'Streaming' : 'Offline'}
                    </div>
                    <div className="text-[11px] font-medium text-neutral-500 mt-3 flex items-center gap-1.5 bg-neutral-50 px-2 py-1 rounded-lg self-start">
                        <Clock size={12} className="text-neutral-400" />
                        <span>Live Sync Active</span>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex flex-col gap-6">
                {/* World Map Section - Full Width */}
                <div className="bg-white border border-neutral-100 rounded-2xl shadow-sm overflow-hidden p-1">
                    <LiveWorldMap visitors={activeVisitors} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Event Ticker Section */}
                    <div className="lg:col-span-4 h-[670px] min-h-[400px]">
                        <EventTicker events={recentEvents} />
                    </div>

                    {/* Charts Section */}
                    <div className="lg:col-span-8">
                        <RealtimeCharts events={recentEvents} visitors={activeVisitors} />
                    </div>
                </div>
            </div>

        </div>
    );
};
